/**
 * F-635: the ONE place that decides what on this device belongs to a signed-in person.
 *
 * The patient app kept the last user (`CapacitorStorage.user`) and everything cached for them
 * (`dailyStatus`, `otherTherapiesHealth_<id>`, goals, followers, therapy/health caches in the
 * Ionic store, ...) and never compared it with the session, so the next person in the same
 * browser saw - and requested by id - the previous person's data.
 *
 * Per-user keys are written in dozens of places, several with dynamic names
 * (`otherTherapiesHealth_<id>`, `connectedDevices_<id>`, a device type, a weather cache keyed
 * by coordinates), so an allow-list of keys to clear would go stale the moment someone adds a
 * cache. The list below is the opposite: the few Preferences keys that are NOT a person's data
 * (device identity, session credentials, a sign-in still in progress, an invite link opened
 * before sign-in, cached public legal documents). Everything else is per-user and is cleared.
 * Adding a key that must survive a change of user means adding it here, on purpose.
 */
import { Preferences } from "@capacitor/preferences";
import { initStorage } from "./storageService";

export const DEVICE_SCOPED_PREFERENCE_KEYS: readonly string[] = [
  // The device itself
  "fcmToken",
  "deviceId",
  // Session credentials (tokenStore). On a change of user they ARE the session /me answered
  // for; signed-out cases clear them through tokenStore.clearTokens().
  "authToken",
  "refreshToken",
  "tokenStoreInstalled",
  // A sign-in in progress - written before there is a user (Login, VerifyLogin, auth.ts,
  // cognitoOauth.ts, useTermsAgreement). VerifyLogin removes its own once the code is accepted.
  "kernelLoginSession",
  "kernelLoginIdentifier",
  "kernelLoginMethod",
  "pkce_verifier",
  "oauth_state",
  "oauth_provider",
  "authMethod",
  "email",
  "phone",
  "CountryCode",
  "token",
  // (F-651: `pendingRegistration` is NOT here. It holds a phone number, verifyCode removes it,
  // and one left behind belongs to someone else, so a change of user clears it.)
  "termsAgreed",
  "termsAgreedDate",
  // A share invite opened from a link before sign-in (App.tsx); consumed after sign-in.
  "invitation_token",
  // Cached public legal documents (TermsAgreementModal, PrivacyPolicy).
  "termsOfUse",
  "privacyPolicy",
];

/**
 * Clear every per-user Preferences key (everything not in DEVICE_SCOPED_PREFERENCE_KEYS) and
 * the Ionic store (IndexedDB on web, SQLite on native), which holds only cached per-user data:
 * daily status, therapy/titration/adherence/curve caches, devices, reflections, notifications.
 * Used on a change of user, when there is no session, and by sign-out.
 */
export const clearUserScopedState = async (): Promise<void> => {
  const keep = new Set(DEVICE_SCOPED_PREFERENCE_KEYS);
  const { keys } = await Preferences.keys();
  await Promise.all(
    keys.filter((key) => !keep.has(key)).map((key) => Preferences.remove({ key })),
  );
  try {
    const store = await initStorage(null);
    await store.clear();
  } catch (error) {
    console.error("clearUserScopedState: the cached-data store could not be cleared", error);
  }
};

/** Does a stored user belong to the profile the kernel session names? */
export const isSessionUser = (storedUser: any, me: any): boolean =>
  storedUser?.profile_id != null &&
  me?.id != null &&
  Number(storedUser.profile_id) === Number(me.id);
